import { useState } from "react";
import { styled } from "styled-components";

import { ErrorMessage, UIButton } from "common/components";
import { isEmail } from "common/utilities/isEmail";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { setCustomMapFields } from "domains/createMapDetail/actions";
import { getCurrentMap, getMapIsPending } from "domains/createMapDetail/selectors";
import { inviteMapSharing } from "domains/createShareSettings/actions";
import { InvitationInput } from "domains/createShareSettings/components/InvitationInput";
import { getInvitationError, getInvitationPending } from "domains/createShareSettings/selectors";
import type { Invite } from "domains/createShareSettings/types";

export const InvitationBox = () => {
  const dispatch = useDispatch();
  const map = useSelector(getCurrentMap);
  const error = useSelector(getInvitationError);
  const isPending = useSelector(getInvitationPending);
  const isMapPending = useSelector(getMapIsPending);
  const [invitations, setInvitations] = useState<Invite[]>([]);
  const translate = useTranslate();

  const getSetInvitation = (index: number) => (newItem: Invite) => {
    const newInvitations: Invite[] =
      index === invitations.length
        ? [...invitations, newItem]
        : newItem.email.length <= 0
          ? invitations.toSpliced(index, 1)
          : invitations.toSpliced(index, 1, newItem);
    if (map?.id === undefined) dispatch(setCustomMapFields({ invitations: newInvitations }));
    setInvitations(newInvitations);
  };

  const onClickInvite = () => {
    if (!isValid || map?.id === undefined) return;
    dispatch(inviteMapSharing({ invitations, mapId: map.id }));
    setInvitations([]);
  };

  const isValid = invitations.length > 0 && invitations.every((item) => isEmail(item.email));

  return (
    <Container>
      {[...invitations, undefined].map((item, index) => (
        <InvitationInput invitation={item} isFirst={index === 0} key={index} setInvitation={getSetInvitation(index)} />
      ))}
      {!isMapPending &&
        (map?.id === undefined ? (
          <InvitationMessage>{translate("app.create.draft_invite_message")}</InvitationMessage>
        ) : (
          <UIButton
            disabled={!isValid}
            isLoading={isPending}
            onClick={onClickInvite}
            style={{ marginTop: "1rem" }}
            variant="filled"
          >
            {translate("app.create.share_settings.invite_user")}
          </UIButton>
        ))}
      <StyledErrorMessage isActive={error}>{translate("app.error.unknown.title")}</StyledErrorMessage>
    </Container>
  );
};

const Container = styled.div`
  margin-top: 10px;
`;

const InvitationMessage = styled.div`
  color: ${({ theme }) => theme.colors.textFade};
  font-size: 14px;
  margin-top: 16px;
`;

const StyledErrorMessage = styled(ErrorMessage)`
  margin: 12px 0 0 0;
`;
